import React, { Component } from 'react';
import { lazy, Suspense } from 'react';
import _ from 'lodash';
import { MenuItem, FormControl, Typography } from '@mui/material';
import MainSelect from '@mui/material/Select';
import { __, getCountryNames } from '../functions';
import '../assets/styles/select.css';

const ReactCountryFlag = lazy(() =>
    import('react-country-flag').then(module => ({
        default: (props) => <module.default {...props} />,
    }))
);

/**
 * Select komponenta.
 */
class Select extends Component {
    /**
     * Default props.
     *
     * @type {{
     *     onChange: function(),
     *     options: {},
     *     label: string,
     *     value: string,
     *     allowEmpty: boolean,
     *     placeholder: null|string,
     *     variant: string,
     *     disabled: boolean,
     *     strict: boolean,
     *     className: string,
     * }}
     */
    static defaultProps = {
        label: '',
        value: '',
        options: {},
        onChange: () => {},
        allowEmpty: true,
        placeholder: null,
        variant: 'outlined',
        disabled: false,
        strict: true,
        className: '',
        multiple: false,
        countries: false,
    };

    /**
     * Default state.
     *
     * @type {{
     *     value: string,
     * }}
     */
    state = {
        value: '',
    };

    country_names = {};

    /**
     * Komponenta bola pripojena.
     */
    componentDidMount() {
        this.country_names = getCountryNames();

        this.setState({ value: this.formatValue(this.props.value) });
    }

    /**
     * Komponenta bola zmenena.
     *
     * @param {Object} prevProps
     * @param {Object} prevState
     * @param snapshot
     */
    componentDidUpdate(prevProps, prevState, snapshot) {
        const value = this.formatValue(this.props.value);

        if (this.state.value !== value) {
            // Nesedi hodnota v state a props, aktualizujeme
            this.setState({ value });
        }
    }

    /**
     * Event po zmene hodnoty.
     *
     * @param {string} value
     */
    onChange(value) {
        const { onChange, multiple } = this.props;

        this.setState({ value });

        if (multiple) {
            value = this.getMultipleValue(value);

            if (value.length > 0) {
                value = _.join(value, '===');
            } else {
                value = '';
            }
        }

        // Zavolame callback
        onChange(value);
    }

    /**
     * Naformatujeme value.
     *
     * @param {string|int} value
     *
     * @return {string}
     */
    formatValue(value) {
        const { strict } = this.props;

        if (strict && (value === 0 || value === '0')) {
            // Nula je prazdna hodnota
            return '';
        }

        return value;
    }

    /**
     * Naformatujeme option.
     *
     * @param {string} option
     * @param {string} key
     *
     * @return {JSX.Element|string}
     */
    formatOption(option, key) {
        const { countries } = this.props;

        if (_.has(this.country_names, option)) {
            option = this.country_names[option];
        }

        if (countries) {
            // Zobrazujeme krajiny
            let country = key;

            if (country === 'en') {
                country = 'us';
            }

            return (
                <div className="select__flag">
                    <Suspense fallback={<div />}><ReactCountryFlag countryCode={country.toUpperCase()} svg /></Suspense>
                    <span>{option}</span>
                </div>
            );
        }

        if (option.indexOf('https://') !== -1) {
            // Zobrazujeme obrazok
            return <img src={option} height={30} alt={option} />;
        }

        return option;
    }

    /**
     * Vratime multiple value.
     *
     * @param {unknown[]} value
     *
     * @return {Array}
     */
    getMultipleValue(value) {
        value = !_.isArray(value) ? _.split(_.toString(value), '===') : value;
        value = _.remove(value, v => v !== '');

        return value.length > 0 ? value : [];
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { label, options, allowEmpty, placeholder, variant, disabled, className, multiple } = this.props;
        const { value } = this.state;

        return (
            <div className={`select ${disabled ? 'disabled' : ''} ${className}`}>
                {label !== '' ? <Typography variant="body2" className="select__label">{label}</Typography> : null}
                <FormControl variant={variant}>
                    <MainSelect
                        className="select__container"
                        value={multiple ? this.getMultipleValue(value) : value}
                        onChange={event => this.onChange(event.target.value)}
                        displayEmpty={placeholder !== null}
                        disabled={disabled}
                        multiple={multiple}
                    >
                        {allowEmpty ? <MenuItem value="">
                            <em>{placeholder !== null ? placeholder : __('Žiadne')}</em>
                        </MenuItem> : null}
                        {_.map(_.keys(options), key => <MenuItem key={key} value={key}>{this.formatOption(options[key], key)}</MenuItem>)}
                    </MainSelect>
                </FormControl>
            </div>
        );
    }
}

export { Select };
